import type { Id, TableNames } from "../lib/api";
import { err, ok, type Result } from "./result";

export type RouteParams = Readonly<Record<string, string | undefined>>;
export type RouteParamIdError =
  | { readonly kind: "missingRouteParam"; readonly key: string }
  | { readonly kind: "invalidRouteParamId"; readonly key: string; readonly value: string };

const ROUTE_ID_RE = /^[A-Za-z0-9_-]+$/;

const cleanRouteParam = (value: string | undefined | null): string | null => {
  const cleaned = value?.trim() ?? "";
  return cleaned === "" ? null : cleaned;
};

// Convex IDs are opaque strings in the browser. The server validates existence
// and table ownership, so this boundary only rejects values that cannot be a
// sane URL-carried ID before applying the client-side brand.
const routeParamId = <TableName extends TableNames>(value: string): Id<TableName> =>
  value as Id<TableName>;

export const routeParamIdResult = <TableName extends TableNames>(
  value: string | undefined | null,
  key: string,
): Result<Id<TableName>, RouteParamIdError> => {
  const cleaned = cleanRouteParam(value);
  if (cleaned === null) return err({ kind: "missingRouteParam", key });
  return ROUTE_ID_RE.test(cleaned)
    ? ok(routeParamId<TableName>(cleaned))
    : err({ kind: "invalidRouteParamId", key, value: cleaned });
};

export const routeIdParam = <TableName extends TableNames>(
  params: RouteParams,
  key: string,
): Id<TableName> | undefined => {
  const result = routeParamIdResult<TableName>(params[key], key);
  return result.ok ? result.value : undefined;
};

export type SearchParams = Pick<URLSearchParams, "get">;

export const searchIdParam = <TableName extends TableNames>(
  params: SearchParams,
  key: string,
): Id<TableName> | null => {
  const result = routeParamIdResult<TableName>(params.get(key), key);
  return result.ok ? result.value : null;
};

export const searchParamsWithValue = (
  params: URLSearchParams,
  key: string,
  value: string,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.set(key, value);
  return next;
};

export const searchParamsWithId = <TableName extends TableNames>(
  params: URLSearchParams,
  key: string,
  id: Id<TableName>,
): URLSearchParams => searchParamsWithValue(params, key, id);

export const searchParamsWithoutKeys = (
  params: URLSearchParams,
  keys: readonly string[],
): URLSearchParams => {
  const next = new URLSearchParams(params);
  for (const key of keys) next.delete(key);
  return next;
};
